import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import type { BalanceEvent, GameId, PassportStampEvent } from '@tg/shared';
import { endpoints } from '@/api/endpoints';
import { paths } from '@/app/paths';
import { GameCover } from '@/art/GameCover';
import { UiIcon } from '@/art/UiIcon';
import { useLocale } from '@/i18n/useLocale';
import { useSocketEvent } from '@/socket/socket';
import { useSession } from '@/stores/session';
import { useToasts } from '@/stores/toasts';
import { cx } from '@/ui';
import { usePassport, useStamped } from './usePassport';
import { gameOfPath, limitsBlock, rememberShown, takeExitByButton, tryMorePick } from './tryMore';
import s from './passport.module.css';

/** a stamp toast: on a game screen short (it must not linger over the round) */
export const STAMP_TOAST_MS = 6_000;
export const STAMP_TOAST_GAME_MS = 4_000;

/**
 * Game Passport runtime: loads the passport for a signed-in player (cleared on sign-out), applies `passport:stamp`
 * and says «New stamp: Garden» (+ a reward line when one was credited) — a quiet toast with a link to the passport;
 * then the «Try another» hint after a session (tryMore.ts).
 */
export function PassportListener() {
  const status = useSession((st) => st.status);
  const intl = useIntl();
  const l = useLocale();
  const { pathname } = useLocation();
  const inGame = gameOfPath(pathname) !== null;

  useEffect(() => {
    if (status === 'user') void usePassport.getState().load();
    else usePassport.getState().clear();
  }, [status]);

  useSocketEvent<[PassportStampEvent]>('passport:stamp', (e) => {
    usePassport.getState().stamp(e);
    const amount = e.rewards.reduce((a, r) => a + r.amount, 0);
    useToasts.getState().push({
      kind: 'success',
      titleId: 'passport.toast.stamp',
      textId: amount > 0 ? 'passport.toast.reward' : undefined,
      values: { game: intl.formatMessage({ id: `common.game.${e.game}` }), amount: intl.formatNumber(amount), num: amount },
      link: { to: paths.passport(l), labelId: 'passport.open' },
      ttl: inGame ? STAMP_TOAST_GAME_MS : STAMP_TOAST_MS,
    });
  });

  return <TryMore />;
}

interface Hint {
  from: GameId;
  games: GameId[];
}

/** «Try another»: watches game sessions (rounds = own debits while on the game screen) and shows the hint after one */
function TryMore() {
  const status = useSession((st) => st.status);
  const stampedSet = useStamped();
  const loaded = usePassport((st) => st.data !== null);
  const { pathname } = useLocation();
  const game = gameOfPath(pathname);
  const session = useRef<{ game: GameId | null; rounds: number }>({ game: null, rounds: 0 });
  const [hint, setHint] = useState<Hint | null>(null);
  const stamped = loaded ? stampedSet : null;
  const latest = useRef({ status, stamped });
  latest.current = { status, stamped };

  useSocketEvent<[BalanceEvent]>('wallet:balance', (e) => {
    if (session.current.game && e.reason === 'bet' && e.delta < 0) session.current.rounds++;
  });

  useEffect(() => {
    const prev = session.current;
    if (prev.game === game) return;
    session.current = { game, rounds: 0 };
    const button = takeExitByButton();
    if (game) {
      setHint(null); // never over a game screen
      return;
    }
    if (!prev.game || latest.current.status !== 'user') return;
    const games = tryMorePick({ from: prev.game, rounds: prev.rounds, byButton: button }, latest.current.stamped, Date.now());
    if (games.length === 0) return;
    let off = false;
    endpoints.limits().then(
      (lim) => {
        if (off || limitsBlock(lim, Date.now())) return;
        rememberShown(Date.now());
        setHint({ from: prev.game!, games });
      },
      () => undefined, // no limits answer — no hint (responsible play first)
    );
    return () => {
      off = true;
    };
  }, [game]);

  useEffect(() => {
    if (status !== 'user') setHint(null);
  }, [status]);

  if (!hint || game) return null;
  return <TryMoreCard hint={hint} onClose={() => setHint(null)} />;
}

export function TryMoreCard({ hint, onClose }: { hint: Hint; onClose: () => void }) {
  const intl = useIntl();
  const l = useLocale();
  const name = (g: GameId) => intl.formatMessage({ id: `common.game.${g}` });
  const reduced = useSession((st) => st.reducedMotion || st.osReducedMotion);
  return (
    <div className={s.tryHost}>
      <aside className={cx(s.try, !reduced && s.tryAnim)} aria-label={intl.formatMessage({ id: 'passport.tryMore.title' })} data-testid="try-more">
        <div className={s.tryHead}>
          <span className={s.tryTitle}>
            <FormattedMessage id="passport.tryMore.title" />
          </span>
          <button type="button" className={s.tryClose} onClick={onClose} aria-label={intl.formatMessage({ id: 'passport.tryMore.close' })}>
            <UiIcon name="close" size={16} fallback="×" />
          </button>
        </div>
        <span className={s.tryLead}>
          <FormattedMessage id="passport.tryMore.lead" values={{ game: name(hint.from) }} />
        </span>
        <div className={s.tryGames}>
          {hint.games.map((g) => (
            <Link key={g} to={paths.game(l, g)} className={s.tryGame} onClick={onClose}>
              <GameCover game={g} width={72} height={45} className={s.tryCover} />
              <span className={s.tryName}>{name(g)}</span>
            </Link>
          ))}
        </div>
      </aside>
    </div>
  );
}
